import { httpResource } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { GpuToastService, GpuToastType } from '@geneplanet/ngx-ui';
import { LanguageService } from 'src/app/shared/services/language.service';
import { environment } from 'src/environments/environment';
import { APIResponseCollection } from '../interfaces/strapi.interface';
import { SystemNotification } from '../interfaces/system-notification.interface';

@Injectable({
  providedIn: 'root',
})
export class SystemNotificationsService {
  private languageService = inject(LanguageService);
  private taostr = inject(GpuToastService);
  private readonly selectedNotification = computed(() =>
    this.notificationsResource.hasValue()
      ? this.findNotification(this.notificationsResource.value())
      : undefined
  );

  private notificationsResource = httpResource<SystemNotification[]>(
    () => ({
      url: `${environment.strapiUrl}/api/system-notifications`,
      params: {
        locale: this.languageService.currentLocation()!.lang.code,
        'filters[$or][0][locations][location][$contains]':
          this.languageService.currentLocation()!.code,
        'filters[$or][1][locations][id][$null]': true,
        status:
          environment.environment === 'production' ? 'published' : 'draft',
        populate: '*',
      },
    }),
    {
      parse: (response) => {
        const data = (response as APIResponseCollection<SystemNotification>)
          ?.data;
        return (data ?? [])
          .filter((item) => item.platform === 'health-intelligence')
          .filter((item) => item.enabled);
      },
      defaultValue: [],
    }
  );

  public readonly currentUrl = signal('');

  constructor() {
    effect(() => {
      const notification = this.selectedNotification();

      this.taostr.dismiss();

      if (notification) {
        this.taostr.show(
          {
            text: notification.content,
            buttonText: notification.buttonText ?? undefined,
          },
          notification.configuration?.type ?? GpuToastType.Warning,
          {
            disableTimeout: notification.configuration?.disableTimeout ?? true,
            closeButton: notification.configuration?.closeButton ?? false,
            enableHtml: true,
            toastClass: 'system-notification-toast gmb-32 gmb-md-0',
          }
        );
      }
    });
  }

  private findNotification(
    notifications: SystemNotification[]
  ): SystemNotification | undefined {
    return notifications
      .filter((item) => this.isNotificationActive(item))
      .find((item) => this.notificationShouldBeVisible(item));
  }

  private isNotificationActive(notification: SystemNotification): boolean {
    const now = new Date();
    if (notification.startDate && now < new Date(notification.startDate)) {
      return false;
    }
    if (notification.endDate && new Date(notification.endDate) <= now) {
      return false;
    }
    return true;
  }

  /**
   * Check if notifications whitelist or blacklist url match
   * @param notification Matching notification
   */
  private notificationShouldBeVisible(
    notification: SystemNotification
  ): boolean {
    if (notification.whitelistUrls.length) {
      return notification.whitelistUrls.some((url) =>
        this.urlMatches(url.path)
      );
    }

    if (notification.blacklistUrls.length) {
      return !notification.blacklistUrls.some((url) =>
        this.urlMatches(url.path)
      );
    }

    return true;
  }

  /**
   * Check if url matches. Supports exact and partial matching.
   *
   *  Exact will match `/categories/nutrition`.
   *
   *  Partial matching can look as `/categories/*`
   *  and will match `/categories/nutrition` and `/categories/cancer-risk`
   */
  private urlMatches(url: string): boolean {
    if (url.slice(-2) === '/*') {
      // Partial matching
      return this.currentUrl().startsWith(url.slice(0, -2));
    } else {
      // exact matching
      return this.currentUrl() === url;
    }
  }
}
